import React, { useMemo } from 'react';
import { ExtrudeGeometry, Shape, Vector3 } from 'three';

import { AnalogLink, AnalogPanel } from '@paragon/contract-library';
import { getPanelStartAndEndPoints, RaycastOrder, RenderOrder } from '@paragon/three-shared';

import { useAppDispatch, useAppSelector } from '../../components/hooks';
import { useClickItem } from '../../store/actions/sceneInteractionActions';
import { clearHoverItemIfActive, ensureHoverItemIsActive } from '../../store/actions/viewportActions';
import { getAnalogModelForActiveComponentDesign } from '../../store/selectors/getAnalogModelForActiveComponentDesignSelector';
import { getSingleSelectedItemEntryOrNull } from '../../store/selectors/getSelectedItemSelector';

const defaultColor = 0xffaf14;
const selectedColor = 0xe74c3c;

function calculateAnalogPanelGeometry(analogLinks: AnalogLink[]) {
  const startAndEndPoints = getPanelStartAndEndPoints(analogLinks);
  if (!startAndEndPoints) return null;
  const { end, start } = startAndEndPoints;
  const thickness = 0.9;
  const toVector = end.clone().sub(start);
  const direction = toVector.clone().normalize();
  const crossVector = direction.clone().cross(new Vector3(0, 0, 1));
  const upVector = crossVector.clone().multiplyScalar(thickness);
  const downVector = upVector.clone().negate();

  const shape = new Shape()
    .lineTo(upVector.x + direction.x, upVector.y + direction.y)
    .lineTo(toVector.x + upVector.x - direction.x, toVector.y + upVector.y - direction.y)
    .lineTo(toVector.x, toVector.y)
    .lineTo(toVector.x + downVector.x - direction.x, toVector.y + downVector.y - direction.y)
    .lineTo(downVector.x + direction.x, downVector.y + direction.y)
    .closePath();
  const extrudeSettings = { steps: 1, depth: 0.7, bevelEnabled: false };
  return new ExtrudeGeometry(shape, extrudeSettings).translate(start.x, start.y, start.z - 0.4);
}

export function AnalogPanelVisual({ analogPanel }: { analogPanel: AnalogPanel }) {
  const { guid } = analogPanel;
  const dispatch = useAppDispatch();
  const selectedEntry = useAppSelector(getSingleSelectedItemEntryOrNull);
  const selected = selectedEntry?.type === 'panel' && selectedEntry.item.guid === guid;

  const { hiddenObjectGuids, showAnalogPanels } = useAppSelector((state) => state.visualOptions);
  const visible = showAnalogPanels && !hiddenObjectGuids.includes(guid);

  const analogModel = useAppSelector(getAnalogModelForActiveComponentDesign);

  const item = { type: 'panel', guid, origin: 'fiber' } as const;

  const geometry = useMemo(() => {
    const panel = analogModel?.panels.find((panel) => panel.guid === guid);
    if (!panel || !analogModel) return undefined;
    const associatedLinks = analogModel.analogLinks.filter(({ guid }) => panel.linkGuids.includes(guid));
    return calculateAnalogPanelGeometry(associatedLinks) ?? undefined;
  }, [analogModel, guid]);

  const onClick = useClickItem({ type: 'panel', item: analogPanel });

  return (
    analogModel && (
      <mesh
        onClick={onClick}
        onPointerOver={(event) => {
          event.stopPropagation();
          dispatch(ensureHoverItemIsActive(item));
        }}
        onPointerOut={() => dispatch(clearHoverItemIfActive(item))}
        userData-raycastOrder={RaycastOrder.AnalogPanelVisual}
        visible={visible}
        geometry={geometry}
        renderOrder={RenderOrder.AnalogPanelVisual}
      >
        <meshBasicMaterial
          transparent
          opacity={selected ? 0.3 : 0.35}
          color={selected ? selectedColor : defaultColor}
        />
      </mesh>
    )
  );
}
